/**
 * API client for backend services
 */

import { auth } from './auth';

/**
 * Get backend URL from environment variable (set at build time)
 * Falls back to localhost for local development
 */
// Backend API URL from environment variable (set at build time)
const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

/**
 * Get authorization header with Google OAuth token
 */
function getAuthHeaders(): Record<string, string> {
  const credential = auth.getGoogleCredential();
  if (credential) {
    return { Authorization: `Bearer ${credential}` };
  }
  return {};
}

/**
 * API endpoints - all backend routes require trailing slashes to avoid HTTP redirect
 */
export const API_ENDPOINTS = {
  newUser: 'https://izh557rlui.execute-api.us-west-1.amazonaws.com/default/ph_new_user',
  getUsers: 'https://kcb86i64d2.execute-api.us-west-1.amazonaws.com/default/ph_get_users',
  // Trailing slashes required to avoid FastAPI redirect (which uses HTTP)
  analysis: `${API_BASE_URL}/analyses/`,
  results: (analysisId: string) => `${API_BASE_URL}/analyses/${analysisId}/results/`,
};

export interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: string | FormData;
  headers?: Record<string, string>;
}

// Guard to prevent multiple simultaneous auth failure redirects
let isRedirecting = false;

/**
 * Handle authentication failure by clearing auth state and redirecting to login.
 * Includes guard against multiple simultaneous redirects when several API calls fail at once.
 * Stores the current path for post-login redirect.
 */
function handleAuthFailure(url: string, status: number): void {
  if (isRedirecting) return;
  isRedirecting = true;

  console.warn(`Authentication failed (${status}) for ${url}, redirecting to login`);

  // Store intended destination for post-login redirect
  const currentPath = window.location.pathname;
  if (currentPath !== '/') {
    sessionStorage.setItem('redirectAfterLogin', currentPath);
  }

  auth.clearAuth();

  // Reset flag after a short delay (allows retry if redirect somehow fails)
  setTimeout(() => {
    isRedirecting = false;
  }, 1000);

  window.location.href = '/';
}

/**
 * Generic JSON fetch utility with error handling.
 * Automatically redirects to login on 401 (unauthorized) or 403 (forbidden) responses.
 */
export async function fetchJSON<T>(url: string, options: FetchOptions = {}): Promise<T | null> {
  try {
    const response = await fetch(url, {
      method: options.method ?? 'GET',
      body: options.body,
      headers: options.headers,
    });

    // Handle authentication failures (expired token, invalid token)
    if (response.status === 401 || response.status === 403) {
      handleAuthFailure(url, response.status);
      return null;
    }

    if (!response.ok) {
      throw new Error(`HTTP error - status: ${response.status}`);
    }

    const data = (await response.json()) as T;
    return data;
  } catch (error) {
    console.error('Error fetching JSON:', error);
    return null;
  }
}

/**
 * Get user's IP address
 */
interface IpResponse {
  ip: string;
}

export async function getIpAddress(): Promise<string | null> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = (await response.json()) as IpResponse;
    return data.ip;
  } catch (error) {
    console.error('Error fetching IP address:', error);
    return null;
  }
}

/**
 * User creation payload
 */
export interface NewUserPayload {
  email: string;
  date: string;
  ip: string;
  [key: string]: string; // Allow additional form fields
}

/**
 * Create a new user
 */
export async function createNewUser(payload: NewUserPayload): Promise<boolean> {
  const result = await fetchJSON(API_ENDPOINTS.newUser, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return result !== null;
}

/**
 * Project/Analysis interfaces
 */
export interface Project {
  id: string;
  patient_id: string;
  project_id: string;
  status: string;
  timepoints: string[];
  qvt_scores: number[] | null;
  created_at: string;
  completed_at: string;
}

/**
 * Get all analysis projects (requires employee auth)
 */
export async function getProjects(): Promise<Project[]> {
  const data = await fetchJSON<Project[]>(API_ENDPOINTS.analysis, {
    headers: getAuthHeaders(),
  });
  return data ?? [];
}

/**
 * Results interfaces
 */
export interface Timepoint {
  timepoint: string;
  label: string;
  qvt_score: number;
  tumor_burden: number;
  img_radiology: string;
  img_qvt: string;
  visualizations: Record<string, { html?: string; png?: string; mp4?: string } | string>;
}

export interface ResultsData {
  patient_id: string;
  timepoints: Timepoint[];
}

/**
 * Get analysis results by ID (requires employee auth)
 */
export async function getResults(analysisId: string): Promise<ResultsData | null> {
  return await fetchJSON<ResultsData>(API_ENDPOINTS.results(analysisId), {
    headers: getAuthHeaders(),
  });
}

/**
 * User interface for admin
 */
export interface User {
  fullName: string;
  email: string;
  orgName: string;
  date: string;
  ip: string;
}

/**
 * Get all registered users (admin only)
 */
export async function getUsers(): Promise<User[]> {
  const data = await fetchJSON<User[]>(API_ENDPOINTS.getUsers);
  return data ?? [];
}
